//#region Util Imports

import * as _ from 'underscore'

//#endregion
import { TemplateHelper, ConfigHelper, LocalizationHelper } from '../../../helpers'
import { EventsNames } from '../../../helpers/events-names'
import { OperatorLayoutSettings } from '../../../helpers/operator-layout-settings'

import { Enums } from '../../../models/common/enums'

import { LoggerService } from '../../common/common.module'
import { GridBridge } from '../../common/common.interfaces'
import { Details, DetailsScope, DetailsAccordion, DetailsAccordionSection } from '../../common/common.details'
import { DetailsParametersOptions } from '../../common/details-parameters/details-parameters.directive'
import { DetailsPopupOptions, DetailsOptions } from '../../common/details-popup/details-popup.directive'
import { GridOptions, GridController, GridTypes } from '../../common/grid/grid.controller'

import { UserSessionService } from '../../../services/user-session'
import { AlarmGroupService } from '../../../services/alarm-group/alarm-group.service';
import { AlarmGroupOperatorService } from '../../../services/alarm-group/alarm-group.operator.service'

import { LocalDictionaryFactory } from '../../api/api.module'

import { Activity } from '../../../models/common/activity'
import { OpAlarmGroup } from '../../../models/core/op-alarm-group'
import { OpDataTypeInGroup } from '../../../models/core/op-data-type-in-group'
import { OpOperator } from '../../../models/core/op-operator'

import { CommonHelper } from '../../../helpers'

import { IMRConfirmationPopupOptions, IIMRConfirmationPopup, IMRConfirmationPopupInitializedArgs } from '../../../modules/common/confirmation-popup2';
import { ColumnModel } from '../../../modules/api/api.models';
import { OpAlarmGroupOperator } from 'app/models/core/op-alarm-group-operator';

interface AlarmGroupsDetailsScope extends DetailsScope {
    id: number
}

export class AlarmGroupsDetailsController extends Details<AlarmGroupsDetailsScope> {

    //#region Consts

    public get ALARM_GROUP_OPEN_DETAILS_POPUP() { return 'ALARM_GROUP_OPEN_DETAILS_POPUP' }
    public get ALARM_GROUP_CLOSE_DETAILS_POPUP() { return 'ALARM_GROUP_CLOSE_DETAILS_POPUP' }
    public get ALARM_GROUP_COMMON_TAB_NAME() { return 'ALARM_GROUP_COMMON_TAB_NAME' }
    public get ALARM_GROUP_PARAMS_ACCORDION_NAME() { return 'ALARM_GROUP_PARAMS_ACCORDION_NAME' }
    public get ALARM_GROUP_OPERATORS_ACCORDION_NAME() { return 'ALARM_GROUP_OPERATORS_ACCORDION_NAME' }
    public get ALARM_GROUP_OPERATORS_GRID_EDIT_NAME() { return 'alarmGroupOperatorsGrid' }
    public get ALARM_GROUP_OPERATORS_GRID_EDIT_POPUP_ID() { return this.$scope.detailsPopupId + '_ALARM_GROUP_OPERATORS_GRID_EDIT_ID' }
    public get ALARM_GROUP_OPERATORS_GRID_NAME() { return 'ALARM_GROUP_OPERATORS_GRID_NAME' }

    //#endregion

    //#region Members

    private alarmGroup: OpAlarmGroup
    private idAlarmGroup: number

    //#endregion

    constructor(
        $scope: AlarmGroupsDetailsScope,
        $q: ng.IQService,
        $timeout: ng.ITimeoutService,
        logger: LoggerService,
        localDictionary: LocalDictionaryFactory,
        protected userSessionService: UserSessionService,
        private alarmGroupService: AlarmGroupService,
        private alarmGroupOperatorService: AlarmGroupOperatorService,
        protected referenceType: Enums.ReferenceType,
        private moduleName: Enums.SimaxModuleName
    ) {

        super($scope, $timeout, $q, logger, localDictionary, OperatorLayoutSettings.AlarmGroupsPopupTabActive, userSessionService, referenceType)

        this.initializeConfirmationPopup()
        this.setEvents()
        this.setOptions()

        if (this.userSessionService.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_LIST)) {
            this.configureTabs(this.getTabs())
        }
    }

    //#region Events

    private onOpen(options: DetailsOptions): void {
        this.$scope.detailsOpenParams = options

        this.idAlarmGroup = options.referenceObjectId

        this.onClear()
        this.showPopup()
        this.onRefresh()
    }

    private onRefresh(toRefresh: OpAlarmGroup = null): void {
        this.$scope.options.isDataLoaded = false

        if (toRefresh == null) {
            this.alarmGroupService.getAlarmGroup(this.idAlarmGroup)
                .then((alarmGroup: OpAlarmGroup) => {
                    this.initialize(alarmGroup)
                })
                .fail(() => {
                    this.hidePopup()
                })
                .always(() => {
                    this.$scope.options.isDataLoaded = true
                })
        } else {
            this.initialize(toRefresh)
            this.$scope.options.isDataLoaded = true
        }
    }

    private onEditParams(): void {
        this.$scope.$root.$broadcast(
            EventsNames.INITIALIZE_CREATE_EDIT_POPUP,
            this.alarmGroupService.getCreateEditOptions(this.alarmGroup.idAlarmGroup, this.alarmGroup)
        )
    }

    private onEditOperators(): void {
        this.$scope.$broadcast(
            EventsNames.OPEN_GRID_EDIT_POPUP
            + this.ALARM_GROUP_OPERATORS_GRID_EDIT_POPUP_ID,
            'alarmGroupsOperators'
        )
    }

    private onClear(): void {
        this.$scope.popupTitle = ''
        this.$scope.showTabs = false

        this.alarmGroup = null
        this.basicParametersOptions = null
    }

    private onClose(): void {
        this.hidePopup()
    }

    private onConfirmDelete(): void {
        this.alarmGroupService.deleteAlarmGroup(this.alarmGroup.idAlarmGroup)
            .then(() => {
                this.$scope.$root.$broadcast(EventsNames.getEventNameByModule(Enums.SimaxEventCategory._MODULE_NAME__DELETED, this.moduleName))
                this.onClose()
            })
    }

    private onDelete(): void {
        this.confirmationPopup.show()
    }

    private onSubmit(inGrid: any): ng.IPromise<boolean> {
        const deferred: ng.IDeferred<boolean> = this.$q.defer<boolean>()

        this.alarmGroupOperatorService.saveOperatorsGridEditResult(this.alarmGroup, inGrid)
            .then((savedRoute: OpAlarmGroup) => {
                this.onRefresh(savedRoute)

                deferred.resolve(true)
            })
            .fail(() => {
                deferred.resolve(false)
            })

        return deferred.promise
    }

    //#endregion 

    //#region Initialize

    private initialize(alarmGroup: OpAlarmGroup): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        if (alarmGroup != null && (this.alarmGroup == null || this.alarmGroup.idAlarmGroup == alarmGroup.idAlarmGroup)) {
            this.alarmGroup = alarmGroup
            this.$scope.popupTitle = alarmGroup.toString()

            $.when(
                this.initializeBasicParameters(alarmGroup),
                this.initializeOperators(alarmGroup)
            )
                .always(() => {
                    def.resolve()
                })

            this.$scope.showTabs = true
        } else {
            def.resolve()
        }

        return def.promise()
    }

    //#endregion

    //#region Parameters
    public basicParametersOptions: DetailsParametersOptions

    private initializeBasicParameters(alarmGroup: OpAlarmGroup): JQueryPromise<void> {
        const def: JQueryDeferred<void> = $.Deferred<void>()

        this.alarmGroupService.getBasicParametersDataTypeGroup()
            .then((dataTypeInGroup: OpDataTypeInGroup[]) => {
                this.basicParametersOptions = {
                    dataTypes: dataTypeInGroup,
                    referenceType: this.referenceType,
                    referenceObject: alarmGroup,
                    useDynamicValues: true,
                    onDataLoaded: () => {
                        def.resolve()
                    }
                }
            })
            .catch(() => {
                def.reject()
            })

        if (this.isAccordionHidden(this.$scope.ALARM_GROUP_PARAMS_ACCORDION_NAME)) {
            def.resolve()
        }

        return def.promise()
    }
    //#endregion

    //#region Operators

    public operatorsGridOptions: GridOptions
    public operatorsGridEditOptions: GridBridge
    private operatorsGrid: GridController<OpOperator>

    private initializeOperators(alarmGroup: OpAlarmGroup): void {
        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupOperators)) {
            this.initializeOperatorsGrid(alarmGroup)
            this.initializeOperatorsGridEdit(alarmGroup)
        }
    }

    private initializeOperatorsGrid(alarmGroup: OpAlarmGroup): void {

        this.operatorsGridOptions = null

        this.$timeout(() => {
            this.operatorsGridOptions = {
                addCustomFilters: true,
                columnsDictionary: this.alarmGroupOperatorService.getAlarmGroupOperatorsGridColumn(),
                idReferenceType: Enums.ReferenceType.IdOperator,
                name: this.ALARM_GROUP_OPERATORS_GRID_NAME,
                type: GridTypes.NO_SEARCH_BAR_PAGINATED_SMALL,

                buttonsOptions: {
                    enabled: true,
                    isRefreshButonHidden: true
                },

                allowColumnReordering: !this.userSession
                    .getLoggedOperator()
                    .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.AlarmGroupOperators),

                getDataSource: () => {
                    if (alarmGroup.currentAlarmGroupOperators != null) {
                        return _.map(alarmGroup.currentAlarmGroupOperators, ((currentAlarmGroupOperator: OpAlarmGroupOperator) => currentAlarmGroupOperator.operator))
                    }
                },

                onInitialized: (e: any, grid: GridController<OpOperator>) => {
                    this.operatorsGrid = grid
                },

                onRowDoubleClick: () => {
                    this.alarmGroupOperatorService.openOperatorDetails(this.operatorsGrid.selectedItemId, this.operatorsGrid.pageItemsIds)
                },
            }
        }, 200)
    }

    private initializeOperatorsGridEdit(alarmGroup: OpAlarmGroup): void {

        this.alarmGroupOperatorService.getOperatorsGridColumns()
            .then((popupColumns: ColumnModel[]) => {
                this.operatorsGridEditOptions = {
                    columns: popupColumns,
                    gridReferenceId: Enums.ReferenceType.IdOperator,
                    id: this.alarmGroup.idAlarmGroup,
                    items: this.alarmGroupOperatorService.getOperatorGridEditObjectsFromAlarmGroup(alarmGroup),
                    name: this.ALARM_GROUP_OPERATORS_GRID_EDIT_NAME,
                    title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.OperatorsEdit),

                    popupId: this.ALARM_GROUP_OPERATORS_GRID_EDIT_POPUP_ID,

                    allowColumnReordering: !this.userSession
                        .getLoggedOperator()
                        .hasPermission(Activity.WEBSIMAX_COLUMN_REORDER_DISALLOWED, Enums.UIObjectProperties.AlarmGroupOperators),

                    getAvailableDataSourceMethod: this.userSession.getDataProvider().Operator.getOperatorFilter,

                    onSubmit: (added: any[], removed: any[], inGrid: any[]) => {
                        return this.onSubmit(inGrid)
                    }
                }
            })
    }

    //#endregion

    //#region Confirmation Popup

    public confirmationPopupOptions: IMRConfirmationPopupOptions
    private confirmationPopup: IIMRConfirmationPopup

    private initializeConfirmationPopup(): void {
        this.confirmationPopupOptions = {
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETING, this.moduleName),
            content: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__CONFIRM_DELETE, this.moduleName),

            onInitialized: (e: IMRConfirmationPopupInitializedArgs) => {
                this.confirmationPopup = e.component
            },

            onConfirmed: () => {
                this.onConfirmDelete()
            }
        }
    }

    //#endregion

    //#region Setup

    public getTabs(): any[] {
        const tabs: any[] = []

        if (!this.userSessionService.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupParameters)
            || !this.userSessionService.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupOperators)
        ) {
            tabs.push({
                template: TemplateHelper.getIconWithText(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Common), ConfigHelper.Icon.HOME, this.userSession),

                name: this.ALARM_GROUP_COMMON_TAB_NAME
            })
        }

        return tabs
    }

    public getAccordions(): DetailsAccordion[] {
        const accordions: DetailsAccordion[] = []

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupParameters)) {
            const items: DetailsAccordionSection[] = []

            items.push({
                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupParameters),

                name: this.ALARM_GROUP_PARAMS_ACCORDION_NAME,
                showInTab: this.ALARM_GROUP_COMMON_TAB_NAME,
                showStatus: false,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Parameters),
                visible: true,

                editMethod: () => {
                    if (this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT)
                        && !this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupParameters)) {
                        this.onEditParams()
                    }
                }
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.AlarmGroupsDetailsAccordions, 0, items))
        }

        if (!this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_HIDDEN_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupOperators)) {
            const items: DetailsAccordionSection[] = []

            items.push({
                canEdit: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT)
                    && !this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupOperators),

                name: this.ALARM_GROUP_OPERATORS_ACCORDION_NAME,
                showInTab: this.ALARM_GROUP_COMMON_TAB_NAME,
                showStatus: true,
                title: LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Operators),
                visible: true,

                status: () => {
                    return `${LocalizationHelper.getLocalizedText(LocalizationHelper.Text.Records)}: ${_.size(this.alarmGroup.currentAlarmGroupOperators)}`
                },

                editMethod: () => {
                    if (this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_EDIT)
                        && !this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DETAILS_READONLY_UI_OBJECT_PROPERTIES, Enums.UIObjectProperties.AlarmGroupOperators)) {
                        this.onEditOperators()
                    }
                }
            })

            accordions.push(new DetailsAccordion(this.$scope, this.$timeout, OperatorLayoutSettings.AlarmGroupsDetailsAccordions, 1, items))
        }
        return accordions
    }

    private setEvents(): void {
        this.$scope.$on(this.ALARM_GROUP_OPEN_DETAILS_POPUP + this.$scope.id, (event: ng.IAngularEvent, ...args: any[]) => {
            args[3] != null ? this.onOpen(args[3]) : null
        })

        this.$scope.$on(EventsNames.CLOSE_CREATE_EDIT_POPUP, (event: ng.IAngularEvent, ...args: any[]) => {
            args[0] != null ? this.onRefresh(args[0]) : null
        })

        this.$scope.$on(this.ALARM_GROUP_CLOSE_DETAILS_POPUP + this.$scope.id, () => { this.onClose() })
    }

    private setOptions(): void {
        this.$scope.popupFor = CommonHelper.alarmsGroup
        this.accordionsNameToShow = this.ALARM_GROUP_COMMON_TAB_NAME

        this.$scope.options = <DetailsPopupOptions>{
            title: LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DETAILS, this.moduleName),
            openEventName: this.ALARM_GROUP_OPEN_DETAILS_POPUP,
            closeEventName: this.ALARM_GROUP_CLOSE_DETAILS_POPUP,
            delHasPermissions: this.userSession.getLoggedOperator().hasPermission(Activity.ALARM_GROUP_DEL) ? Activity[Activity.ALARM_GROUP_DEL] : null,
            id: this.$scope.id,

            deleteMethod: () => this.onDelete(),
            getAccordionDataMethod: () => { return this.getAccordionData() },
            getAccordionSelectionMethod: () => { return this.getAccordionSelection() },
            updateAccordionMethod: (names: any[]) => { return this.updateAccordions(names) }
        }

    }
    //#endregion

    public resetData(): void {
        this.onClear()
    }

    public loadData(id: number, objects: any[]): void {
        this.onOpen(null)
    }

    public updateData(result: any): void {
        this.onRefresh(result)
    }
}